import {
  Blockquote,
  buildFormValuesFromFields,
  buildPayloadFromFields,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  FormBuilder,
  getResolver,
  Label,
  StepperFooter,
  useStepperContext,
} from '@korint/folio';
import { useQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  keyValueFieldsToObject,
  useBrokerageFirmControllerUpdateBrokerageFirm,
} from 'korint-sdk';
import type { Field } from 'korint-sdk/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

const allClientInfoFields: Array<Field> = [
  {
    name: 'isPartOfDistributionNetworkGroup',
    required: false,
    type: 'checkbox',
    isCustomField: true,
  },
  {
    name: 'garageEmployeeCount',
    required: true,
    type: 'number',
    isCustomField: true,
  },
  {
    name: 'distributionNetworkGroupName',
    required: true,
    type: 'text',
    isCustomField: true,
  },
] as const;

const independentClientInfoFields: Array<Field> = allClientInfoFields.filter(
  field => field.name !== 'distributionNetworkGroupName' && field.name !== 'garageEmployeeCount',
);

const contactInfoFields: Array<Field> = [
  {
    name: 'contactFirstName',
    required: true,
    type: 'text',
    isCustomField: true,
  },
  {
    name: 'contactLastName',
    required: true,
    type: 'text',
    isCustomField: true,
  },
  {
    name: 'contactEmail',
    required: true,
    type: 'email',
    isCustomField: true,
  },
  {
    name: 'contactPhoneNumber',
    required: true,
    type: 'phoneNumber',
    isCustomField: true,
  },
] as const;

const ceoInfoFields: Array<Field> = [
  {
    name: 'directorFirstName',
    required: true,
    type: 'text',
    isCustomField: true,
  },
  {
    name: 'directorLastName',
    required: true,
    type: 'text',
    isCustomField: true,
  },
  {
    name: 'directorEmail',
    required: true,
    type: 'email',
    isCustomField: true,
  },
  {
    name: 'directorPhoneNumber',
    required: true,
    type: 'phoneNumber',
    isCustomField: true,
  },
  {
    name: 'directorPosition',
    required: true,
    type: 'text',
    isCustomField: true,
  },
] as const;

const allFields = [...allClientInfoFields, ...contactInfoFields, ...ceoInfoFields];

export function BrokerageFirmInformations() {
  const [clientInfoFields, setClientInfoFields] = useState(independentClientInfoFields);

  const [fillInformation, setFillInformation] = useState(false);

  const { brokerageFirmId } = useParams();
  const stepper = useStepperContext();

  const { mutateAsync: updateBrokerageFirm } = useBrokerageFirmControllerUpdateBrokerageFirm({
    mutation: {
      onError: error => {
        // biome-ignore lint/suspicious/noConsole: <explanation>
        console.log(error);
      },
    },
  });

  const { data: brokerageFirm, isLoading: isBrokerageFirmLoading } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId!),
    enabled: !!brokerageFirmId,
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const form = useForm({
    values: buildFormValuesFromFields(allFields, brokerageFirm),
    resolver: getResolver([...clientInfoFields, ...contactInfoFields, ...ceoInfoFields]),
  });

  const isPartOfDistributionNetworkGroup = form.watch(
    'isPartOfDistributionNetworkGroup',
  ) as boolean;

  useEffect(() => {
    if (isPartOfDistributionNetworkGroup) {
      setClientInfoFields(allClientInfoFields);
    } else {
      setClientInfoFields(independentClientInfoFields);
    }
  }, [isPartOfDistributionNetworkGroup]);

  useEffect(() => {
    if (fillInformation) {
      form.setValue('directorFirstName', form.getValues('contactFirstName'));
      form.setValue('directorLastName', form.getValues('contactLastName'));
      form.setValue('directorEmail', form.getValues('contactEmail'));
      form.setValue('directorPhoneNumber', form.getValues('contactPhoneNumber'));
    }
  }, [fillInformation]);

  return (
    <div className="flex flex-col gap-4">
      <Blockquote className="bg-blue-100 border-blue-400">
        Pour pouvoir distribuer le produit panne mécanique, veuillez compléter les informations
        ci-dessous.
      </Blockquote>
      <Card>
        <CardHeader>
          <CardTitle>Informations du garage</CardTitle>
        </CardHeader>
        <CardContent>
          <FormBuilder fields={clientInfoFields} form={form} isLoading={isBrokerageFirmLoading} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Informations de contact</CardTitle>
        </CardHeader>
        <CardContent>
          <FormBuilder fields={contactInfoFields} form={form} isLoading={isBrokerageFirmLoading} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Informations du dirigeant</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center mb-4">
            <Checkbox
              id="fillInformation"
              value="Remplir les informations du dirigeant avec les informations de contact"
              defaultChecked={fillInformation}
              onCheckedChange={checked => setFillInformation(!!checked)}
            />
            <Label className="ml-2" htmlFor="fillInformation">
              Remplir les informations du dirigeant avec les informations de contact
            </Label>
          </div>
          <FormBuilder fields={ceoInfoFields} form={form} isLoading={isBrokerageFirmLoading} />
        </CardContent>
      </Card>
      <StepperFooter
        nextButtonEnabled
        onNext={form.handleSubmit(async data => {
          if (!brokerageFirm) return;

          await updateBrokerageFirm({
            brokerageFirmId: brokerageFirm.id,
            data: buildPayloadFromFields(allFields, data),
          });

          stepper.next();
        })}
        loading={form.formState.isSubmitting}
      />
    </div>
  );
}
